import {
  Building2,
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PresenceBadge } from '@/features/presence';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { BASE as EMPLOYEES_BASE } from '../api';
import {
  EMPLOYEE_STATUSES,
  employeeStatusLabel,
  employeeStatusVariant,
  employmentTypeLabel,
} from '../constants';
import { useEmployees } from '../queries';
import { useEmployeesUi, type EmployeesViewMode } from '../store/viewPrefs';
import { employeeName, type Employee } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type EmployeeOrderingColumn = 'last_name' | 'job_title' | 'status' | 'hire_date';

const VIEW_MODES: readonly ViewModeOption<EmployeesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
  { value: 'grouped_dept', label: 'Grouped by department', icon: Building2 },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

/** First letters of the first two name parts, for the avatar fallback. */
function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function EmployeesListPage() {
  const navigate = useNavigate();
  const view = useEmployeesUi((s) => s.view);
  const setView = useEmployeesUi((s) => s.setView);
  const ordering = useEmployeesUi((s) => s.ordering);
  const setOrdering = useEmployeesUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useEmployees({
    page,
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    // Roster shows everyone (incl. terminated); pickers/forms hide terminated.
    include_terminated: true,
    ordering,
  });

  const results = data?.results ?? [];
  const open = (e: Employee) => navigate(`/hrm/employees/${e.public_id}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Employees</h1>
        <Button onClick={() => navigate('/hrm/employees/new')}>
          <Plus /> New employee
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by name or email…"
              className="pl-8"
              aria-label="Search employees"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {EMPLOYEE_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `employees.csv` without leaving the SPA.
              void exportCsv(EMPLOYEES_BASE, {
                search: search || undefined,
                status: status === 'all' ? undefined : status,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export employees as CSV"
            title="Export employees as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <EmployeesSkeleton />
      ) : results.length === 0 ? (
        <EmployeesTable
          employees={[]}
          onRowClick={() => undefined}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <EmployeesTable
          employees={results}
          onRowClick={open}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <EmployeesList employees={results} onOpen={open} />
      ) : view === 'cards' ? (
        <EmployeesCards employees={results} onOpen={open} />
      ) : view === 'grouped_dept' ? (
        <EmployeesGroupedByDepartment employees={results} onOpen={open} />
      ) : (
        <EmployeesGroupedByStatus employees={results} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'employee' : 'employees'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  employees: Employee[];
  onOpen: (e: Employee) => void;
}

interface EmployeesTableProps {
  employees: Employee[];
  onRowClick: (e: Employee) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function EmployeesTable({
  employees,
  onRowClick,
  ordering,
  onOrderingChange,
}: EmployeesTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<EmployeeOrderingColumn>
              column="last_name"
              value={ordering}
              onChange={onOrderingChange}
            >
              Name
            </SortableTableHead>
            <SortableTableHead<EmployeeOrderingColumn>
              column="job_title"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Job title
            </SortableTableHead>
            <TableHead className="hidden md:table-cell">Type</TableHead>
            <SortableTableHead<EmployeeOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<EmployeeOrderingColumn>
              column="hire_date"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden lg:table-cell"
            >
              Hired
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No employees found.
              </TableCell>
            </TableRow>
          ) : (
            employees.map((emp) => (
              <TableRow key={emp.id} className="cursor-pointer" onClick={() => onRowClick(emp)}>
                <TableCell className="font-medium">{employeeName(emp)}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {emp.job_title || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {employmentTypeLabel(emp.employment_type)}
                </TableCell>
                <TableCell>
                  <Badge variant={employeeStatusVariant(emp.status)}>
                    {employeeStatusLabel(emp.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {formatDate(emp.hire_date)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function EmployeesCards({ employees, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {employees.map((emp) => (
        <Card
          key={emp.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(emp)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(emp);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <div className="flex items-center gap-3">
              <Avatar className="size-9">
                {emp.photo ? <AvatarImage src={emp.photo} alt="" /> : null}
                <AvatarFallback className="text-xs">{initials(employeeName(emp))}</AvatarFallback>
                <PresenceBadge user={emp.user} />
              </Avatar>
              <CardTitle className="min-w-0 truncate text-base">{employeeName(emp)}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={employeeStatusVariant(emp.status)}>
              {employeeStatusLabel(emp.status)}
            </Badge>
            {emp.job_title ? (
              <span className="text-muted-foreground truncate">{emp.job_title}</span>
            ) : null}
            <span className="text-muted-foreground ml-auto">{formatDate(emp.hire_date)}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EmployeesList({ employees, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {employees.map((emp) => (
        <li key={emp.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(emp)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
              {employeeName(emp)}
            </span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {emp.job_title || '—'}
            </span>
            <Badge variant={employeeStatusVariant(emp.status)} className="shrink-0">
              {employeeStatusLabel(emp.status)}
            </Badge>
            <span className="text-muted-foreground text-xs">{formatDate(emp.hire_date)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function EmployeesGroupedByStatus({ employees, onOpen }: ListProps) {
  // Bucket by status, preserving the declared status order; skip empty groups.
  const groups = useMemo(() => {
    return EMPLOYEE_STATUSES.map((s) => ({
      key: s.value,
      label: s.label,
      bucket: employees.filter((e) => e.status === s.value),
    }));
  }, [employees]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`employees-status-${key}`}>
            <h2
              id={`employees-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <EmployeesCards employees={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function EmployeesGroupedByDepartment({ employees, onOpen }: ListProps) {
  // Bucket by department name; unassigned employees fall into "No department",
  // shown last. Named departments are sorted alphabetically.
  const groups = useMemo(() => {
    const byDept = new Map<string, Employee[]>();
    for (const e of employees) {
      const key = e.department_name ?? '';
      const bucket = byDept.get(key);
      if (bucket) bucket.push(e);
      else byDept.set(key, [e]);
    }
    const named = [...byDept.entries()]
      .filter(([key]) => key !== '')
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, bucket]) => ({ key: label, label, bucket }));
    const none = byDept.get('');
    if (none) named.push({ key: '__none__', label: 'No department', bucket: none });
    return named;
  }, [employees]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key} aria-labelledby={`employees-dept-${key}`}>
          <h2
            id={`employees-dept-${key}`}
            className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
          >
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <EmployeesCards employees={bucket} onOpen={onOpen} />
        </section>
      ))}
    </div>
  );
}

function EmployeesSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
